import HomeModel from './home-model';
import HomeView from './home-view';
import TransitionHelper from '../../utils/transition';
import * as UIService from '../../services/ui-service';
import AuthService from '../../services/auth-service';

export default class HomePresenter {
  constructor(model, view, appInterface) {
    this.model = model || new HomeModel();
    this.view = view || new HomeView();
    this.appInterface = appInterface || {};
    
    this.isLoadingMore = false;
    this.searchTimeout = null;
    this.isCanvasCameraInitialized = false;
    this.periodicIntervals = [];
  }

  async init() {
    this.model.loadUserState();
    this.setupEventListeners();
    this.setupCleanupListener();
    
    if (this.model.isAuthenticated) {
      await this.loadStories();
    } else {
      this.view.showAuthRequired('#posts-container', () => this.appInterface.navigateTo('auth'));
    }
    
    this.setupPeriodicUpdates();
  }

  setupEventListeners() {
    this.view.setupFormEventListeners(
      () => this.checkPostButtonState(),
      () => this.submitPost(),
      () => this.showImageDialog(),
      () => this.showLocationDialog(),
      () => this.removeImage(),
      () => this.removeLocation()
    );

    this.view.setupImageDialogEventListeners(
      () => this.view.triggerImageInput(),
      (file) => this.handleImageSelect(file),
      () => this.handleSelectCamera(),
      () => this.captureImageFromCanvas(),
      () => this.flipCamera(),
      () => this.cancelCameraDialog()
    );

    this.view.setupLocationDialogEventListeners(
      () => this.getCurrentLocation(),
      () => this.saveLocation(),
      (query) => this.searchLocation(query)
    );

    this.view.setupPostInteractionEventListeners(
      (storyId) => this.toggleBookmark(storyId),
      (storyId) => this.sharePost(storyId),
      (src) => this.showLightbox(src)
    );

    this.view.setupInfiniteScrollEventListener(() => this.handleInfiniteScroll());

    this.view.setupFilterEventListeners(
      () => this.applyFilter('all'),
      () => this.applyFilter('maps')
    );

    this.view.setupShowNewPostsEventListener(() => this.showNewPosts());

    this.view.setupCameraDialogObserver((isOpen) => {
      if (!isOpen) {
        this.view.stopCameraStream();
      }
    });

    this.view.setupPeriodicTimestampUpdates();
  }

  setupPeriodicUpdates() {
    const newPostsInterval = setInterval(() => {
      if (this.model.isAuthenticated && this.model.lastFetchTime) {
        this.checkForNewPosts();
      }
    }, 120000);
    this.periodicIntervals.push(newPostsInterval);
  }

  setupCleanupListener() {
    this.cleanupHandler = () => {
      this.cleanup();
    };
    
    // cleanup on unload handled by hosting environment
  }

  async loadStories() {
    try {
      this.view.showLoading('#posts-container');
      
      const stories = await this.model.getAllStories({ page: 1, size: 10 });
      
      this.model.setStories(stories);
      this.model.updateCurrentOffset(10);
      this.model.setHasMorePosts(stories.length === 10);
      
      this.model.updateLastFetchTime();
      
      if (stories.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(stories);
        this.view.renderStories(storiesWithBookmarkStatus, '#posts-container');
        await this.loadLocationNames();
      } else {
        this.view.showEmptyState('#posts-container', 'Belum ada cerita untuk ditampilkan');
      }
    } catch (error) {
      if (error.message.includes('401')) {
        this.view.showAuthRequired('#posts-container', () => this.appInterface.navigateTo('auth'));
      } else {
        this.view.showEmptyState('#posts-container', 'Gagal memuat cerita');
      }
    }
  }

  async loadMoreStories() {
    if (this.appInterface.getCurrentPage() !== 'home') {
      return;
    }
    
    if (this.isLoadingMore || !this.model.hasMorePosts || !this.model.isAuthenticated) return;
    
    this.isLoadingMore = true;
    
    if (!this.view.isPageActive()) {
      this.isLoadingMore = false;
      return;
    }
    
    this.view.showLoadingMore('#posts-container');
    
    try {
      const page = Math.floor(this.model.currentOffset / 10) + 1;
      const newStories = await this.model.getAllStories({ page, size: 10 });
      
      if (newStories && newStories.length > 0) {
        this.model.appendStories(newStories);
        this.model.updateCurrentOffset(this.model.currentOffset + newStories.length);
        this.model.setHasMorePosts(newStories.length === 10);
        
        this.view.removeLoadingMore();
        
        if (this.appInterface.getCurrentPage() === 'home' && this.view.isPageActive()) {
          const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(newStories);
          this.view.renderStories(storiesWithBookmarkStatus, '#posts-container', true);
          await this.loadLocationNames(newStories);
        }
      } else {
        this.model.setHasMorePosts(false);
        this.view.removeLoadingMore();
      }
    } catch (error) {
      this.model.setHasMorePosts(false);
      this.view.removeLoadingMore();
      this.appInterface.showToast('Gagal memuat lebih banyak cerita', 'error');
    } finally {
      this.isLoadingMore = false;
    }
  }

  addBookmarkStatusToStories(stories) {
    return stories.map(story => ({
      ...story,
      isBookmarked: this.model.isBookmarked(story.id)
    }));
  }

  async loadLocationNames(stories = this.model.stories) {
    const storiesWithLocation = stories.filter(story => story.lat && story.lon);
    
    for (const story of storiesWithLocation) {
      try {
        const locationName = await this.model.getLocationName(story.lat, story.lon);
        this.view.updateLocationName(story.id, locationName);
      } catch (error) {
        this.view.updateLocationName(story.id, 'Lokasi tidak tersedia');
      }
    }
  }

  async checkForNewPosts() {
    if (!this.model.lastFetchTime || !this.model.isAuthenticated) return;

    try {
      const stories = await this.model.getAllStories({ page: 1, size: 10 });
      
      if (stories && stories.length > 0) {
        const newPostsCount = this.model.checkForNewPosts(stories);
        
        if (newPostsCount >= 5) {
          this.view.renderNewPostsTab(newPostsCount);
        }
      }
    } catch (error) {
      
    }
  }

  showNewPosts() {
    this.model.newPostsAvailable = 0;
    this.view.hideNewPostsTab();
    this.refreshStories();
    this.view.scrollToTop();
  }

  async refreshStories() {
    this.model.resetPagination();
    await this.loadStories();
  }

  checkPostButtonState() {
    const description = this.view.getFormDescription();
    const hasContent = description.length > 0;
    const hasImage = this.model.getSelectedImage() !== null;
    const canSubmit = hasContent && hasImage;
    
    this.view.updatePostButtonState(canSubmit);
  }

  async handleImageSelect(file) {
    if (!file) {
      return;
    }

    if (file.size > 1024 * 1024) {
      this.appInterface.showToast('Ukuran file terlalu besar. Maksimal 1MB.', 'error');
      this.view.clearImageInput();
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.appInterface.showToast('File harus berupa gambar.', 'error');
      this.view.clearImageInput();
      return;
    }

    this.model.setSelectedImage(file);
    
    const reader = new FileReader();
    reader.onload = (eventReader) => {
      this.view.addImagePreview(eventReader.target.result);
      this.checkPostButtonState();
      this.appInterface.hideDialog('image-dialog');
      this.appInterface.showToast('Foto berhasil dipilih', 'success');
    };
    
    reader.onerror = () => {
      this.appInterface.showToast('Gagal membaca file gambar.', 'error');
      this.view.clearImageInput();
      this.model.clearSelectedImage();
    };

    setTimeout(() => {
      this.view.clearImageInput();
    }, 100);
    
    reader.readAsDataURL(file);
  }

  removeImage() {
    this.model.clearSelectedImage();
    this.view.removeImagePreview();
    this.checkPostButtonState();
  }

  removeLocation() {
    this.model.clearSelectedLocation();
    this.view.removeLocationPreview();
  }

  async handleSelectCamera() {
    this.appInterface.switchDialog('image-dialog', 'camera-canvas-dialog', async () => {
      try {
        await this.prepareCamerasForDialog();
        
        if (this.model.videoDevices.length === 0) {
          this.appInterface.showToast('Tidak ada kamera yang tersedia', 'error');
          return;
        }
        
        await this.initCanvasCamera();
      } catch (error) {
        this.appInterface.showToast('Gagal memulai kamera: ' + error.message, 'error');
        this.appInterface.hideDialog('camera-canvas-dialog');
      }
    });
  }

  async captureImageFromCanvas() {
    try {
      const imageFile = await this.view.captureImageFromCanvas();
      
      this.model.setSelectedImage(imageFile);

      const reader = new FileReader();
      reader.onload = (e) => {
        this.view.addImagePreview(e.target.result);
        this.checkPostButtonState();
      };
      reader.readAsDataURL(imageFile);
      
      this.appInterface.showToast('Foto berhasil diambil!', 'success');
      
      // PENTING: Stop camera dan tutup dialog setelah capture
      this.view.stopCameraStream();
      this.appInterface.hideDialog('camera-canvas-dialog');
    } catch (error) {
      this.appInterface.showToast('Gagal mengambil gambar: ' + error.message, 'error');
    }
  }

  cancelCameraDialog() {
    this.view.stopCameraStream();
    this.appInterface.hideDialog('camera-canvas-dialog');
  }

  async prepareCamerasForDialog() {
    try {
      this.model.videoDevices = await this.model.prepareCamerasForDialog();
      this.updateFlipCameraButtonVisibility();
      return this.model.videoDevices;
    } catch (error) {
      throw new Error('Gagal menyiapkan kamera: ' + error.message);
    }
  }

  updateFlipCameraButtonVisibility() {
    if (this.model.videoDevices && this.model.videoDevices.length > 1) {
      this.view.enableFlipCameraButton();
    } else {
      this.view.disableFlipCameraButton();
    }
  }
  
  updateFlipCameraButtonVisibility() {
    if (this.model.videoDevices && this.model.videoDevices.length > 1) {
      this.view.enableFlipCameraButton();
    } else {
      this.view.disableFlipCameraButton();
    }
  }

  async flipCamera() {
    if (this.model.videoDevices.length <= 1) {
      this.appInterface.showToast('Tidak ada kamera lain untuk diganti', 'info');
      return;
    }

    try {
      this.model.currentCameraIndex = (this.model.currentCameraIndex + 1) % this.model.videoDevices.length;
      this.appInterface.showToast('Mengganti kamera...', 'info', 1500);
      await this.initCanvasCamera();
    } catch (error) {
      this.appInterface.showToast('Gagal mengganti kamera: ' + error.message, 'error');
    }
  }
  
  async initCanvasCamera() {
    try {
      const stream = await this.model.getUserMediaStream(this.model.currentCameraIndex);
      
      this.view.initCameraStream(stream, () => {
        this.isCanvasCameraInitialized = true;
      });
    } catch (err) {
      throw new Error('Tidak bisa mengakses kamera: ' + err.message);
    }
  }

  async getCurrentLocation() {
    this.view.showCurrentLocationLoading();

    try {
      const location = await this.model.getUserLocation();
      this.model.setSelectedLocation(location);
      
      if (this.view.locationMap) {
        this.view.setMapCenter(location.lon, location.lat, 15);
        this.view.addLocationMarker(location.lon, location.lat);
      }
      
      this.view.enableSaveLocationButton();
      this.view.hideCurrentLocationLoading();
      
      this.appInterface.showToast('Lokasi berhasil didapat', 'success');
    } catch (error) {
      this.view.hideCurrentLocationLoading();
      this.appInterface.showToast('Gagal mendapatkan lokasi Anda', 'error');
    }
  }

  initLocationMap() {
    if (this.view.locationMap) return;
    
    try {
      const isDark = this.view.isDarkMode();
      const config = this.model.getLocationMapConfig(isDark);

      this.view.initLocationMap(config, (lat, lng) => {
        this.model.setSelectedLocation({ lat, lon: lng });
        this.view.addLocationMarker(lng, lat);
        this.view.enableSaveLocationButton();
      });

      this.view.setupMapLayerSwitch((layerType) => {
        const isDark = this.view.isDarkMode();
        const styleUrl = this.model.getMapStyleConfig(isDark, layerType);
        this.view.setMapStyle(styleUrl);
      });

      const selectedLocation = this.model.getSelectedLocation();
      if (selectedLocation) {
        this.view.addLocationMarker(selectedLocation.lon, selectedLocation.lat);
        this.view.setMapCenter(selectedLocation.lon, selectedLocation.lat, 15);
        this.view.enableSaveLocationButton();
      }

      this.model.getUserLocation().then(location => {
        if (location) {
          this.view.setMapCenter(location.lon, location.lat, 13);
        }
      }).catch(() => {});
    } catch (error) {
      this.appInterface.showToast('Gagal memuat peta', 'error');
    }
  }

  async searchLocation(query) {
    if (!query || query.length < 3) {
      this.view.clearLocationResults();
      return;
    }
    
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(async () => {
      try {
        const data = await this.model.searchLocation(query);
        
        this.view.clearLocationResults();
        
        if (data.features && data.features.length > 0) {
          this.view.showLocationResults(data.features, (lat, lon, placeName) => {
            this.handleLocationResultClick(lat, lon, placeName);
          });
        }
      } catch (error) {
        this.view.clearLocationResults();
        this.appInterface.showToast('Gagal mencari lokasi', 'error');
      }
    }, 300);
  }

  handleLocationResultClick(lat, lon, placeName) {
    if (this.view.locationMap) {
      this.view.setMapCenter(lon, lat, 15);
      this.model.setSelectedLocation({ lat, lon });
      this.view.addLocationMarker(lon, lat);
      this.view.enableSaveLocationButton();
    }
    
    this.view.setLocationSearchValue(placeName);
    this.view.clearLocationResults();
  }

  async saveLocation() {
    const selectedLocation = this.model.getSelectedLocation();
    if (!selectedLocation) return;
    
    try {
      const locationName = await this.model.getLocationName(selectedLocation.lat, selectedLocation.lon);
      this.view.addLocationPreview(locationName, selectedLocation.lat, selectedLocation.lon);
    } catch (error) {
      this.view.addLocationPreview('Lokasi tidak diketahui', selectedLocation.lat, selectedLocation.lon);
    }
    
    this.view.disableSaveLocationButton();
    
    this.appInterface.hideDialog('location-dialog');
    this.appInterface.showToast('Lokasi berhasil ditambahkan', 'success');
  }

  async submitPost() {
    const description = this.view.getFormDescription();
    
    if (!description) {
      this.appInterface.showToast('Postingan harus berisi teks', 'error');
      return;
    }

    if (!this.model.getSelectedImage()) {
      this.appInterface.showToast('Postingan harus berisi gambar', 'error');
      return;
    }

    const storyData = this.model.getFormData(description);
    
    try {
      this.view.setSubmitButtonLoading(true);

      const response = await this.model.addStory(storyData);

      if (this.model.isAuthenticated) {
        this.appInterface.showToast('Cerita berhasil dibuat!', 'success');
        await TransitionHelper.fadeTransition(async () => {
          await this.refreshStories();
        });
      } else {
        this.appInterface.showToast('Cerita berhasil dibuat! Masuk untuk melihat postingan Anda.', 'success');
      }
      
      this.view.clearForm();
      this.model.clearForm();
      this.checkPostButtonState();
    } catch (error) {
      this.appInterface.showToast(error.message || 'Gagal membuat cerita. Silakan coba lagi.', 'error');
    } finally {
      this.view.setSubmitButtonLoading(false);
    }
  }

  toggleBookmark(storyId) {
    if (!this.model.isAuthenticated) {
      this.appInterface.showToast('Masuk terlebih dahulu untuk menyimpan postingan', 'warning');
      return;
    }
    
    const isCurrentlyBookmarked = this.model.isBookmarked(storyId);
    
    if (isCurrentlyBookmarked) {
      this.removeBookmark(storyId);
    } else {
      this.addBookmark(storyId);
    }
  }

  addBookmark(storyId) {
    if (!this.model.isAuthenticated) {
      this.appInterface.showToast('Masuk terlebih dahulu untuk menyimpan postingan', 'warning');
      return;
    }

    const bookmark = this.model.addBookmark(storyId);
    
    if (bookmark) {
      this.updateGlobalBookmarks();
      this.view.updateBookmarkStatus(storyId, true);
      this.appInterface.showToast('Postingan berhasil disimpan', 'success');
    }
  }

  removeBookmark(storyId) {
    const success = this.model.removeBookmark(storyId);
    
    if (success) {
      this.updateGlobalBookmarks();
      this.view.updateBookmarkStatus(storyId, false);
      this.appInterface.showToast('Postingan dihapus dari bookmark', 'success');
    }
  }

  sharePost(storyId) {
    const baseUrl = this.appInterface.getCurrentUrl();
    const url = `${baseUrl}#/post/${storyId}`;
    this.appInterface.setShareUrl(url);
    this.appInterface.showDialog('share-dialog');
  }

  showLightbox(src) {
    this.appInterface.showLightbox(src);
  }

  applyFilter(filterType) {
    if (!this.model.isAuthenticated) {
      this.view.setFilterActive(filterType);
      this.view.showAuthRequired('#posts-container', () => this.appInterface.navigateTo('auth'));
      return;
    }
    
    this.view.setFilterActive(filterType);
    
    if (filterType === 'maps') {
      const storiesWithLocation = this.model.stories.filter(story => story.lat && story.lon);
      if (storiesWithLocation.length > 0) {
        const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(storiesWithLocation);
        this.view.renderStories(storiesWithBookmarkStatus, '#posts-container');
        this.loadLocationNames(storiesWithLocation);
      } else {
        this.view.showEmptyState('#posts-container', 'Tidak ada cerita dengan lokasi');
      }
    } else {
      const storiesWithBookmarkStatus = this.addBookmarkStatusToStories(this.model.stories);
      this.view.renderStories(storiesWithBookmarkStatus, '#posts-container');
      this.loadLocationNames(this.model.stories);
    }
  }

  handleInfiniteScroll() {
    if (!this.isLoadingMore && this.model.hasMorePosts && this.model.isAuthenticated) {
      const scrollData = this.view.getScrollData();
      
      if (scrollData.scrollTop + scrollData.windowHeight >= scrollData.documentHeight - 200) {
        this.loadMoreStories();
      }
    }
  }

  updateGlobalBookmarks() {
    this.appInterface.setBookmarks([...this.model.bookmarks]);
    this.appInterface.updateBookmarkBubble();
  }

  showImageDialog() {
    this.appInterface.showDialog('image-dialog');
  }

  showLocationDialog() {
    this.appInterface.showDialog('location-dialog');
    setTimeout(() => {
      this.initLocationMap();
    }, 300);
  }

  cleanup() {
    this.view.cleanup();
    
    this.periodicIntervals.forEach(interval => {
      clearInterval(interval);
    });
    this.periodicIntervals = [];
    
    if (this.cleanupHandler) {
      this.cleanupHandler = null;
    }
    
    this.isLoadingMore = false;
    this.model.setHasMorePosts(true);
    this.model.resetPagination();
    
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  }

  refresh() {
    this.refreshStories();
  }

  loadUserState() {
    const appState = {
      isAuthenticated: this.appInterface.isAuthenticated(),
      user: this.appInterface.getUser(),
      bookmarks: this.appInterface.getBookmarks()
    };
    this.model.isAuthenticated = appState.isAuthenticated;
    this.model.user = appState.user;
    this.model.bookmarks = appState.bookmarks;
    this.model.loadUserState();
  }
}